import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import MaskInput from 'components/MaskedInput';
import SoilTypeField from 'features/Croptimizer/components/Fields/SoilTypeField';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

interface StatusBoardSoilSectionProps {
  secSmol: string;
  setSecSmol: (value: string) => void;
  initialSecSmol: string;
  errorSecSmol: string | null;
  fineDrySoil: string;
  setFineDrySoil: (value: string) => void;
  handleFineDrySoil: string | null;
  kg: string;
}

export const StatusBoardSoilSection = ({
  secSmol,
  setSecSmol,
  initialSecSmol,
  errorSecSmol,
  fineDrySoil,
  setFineDrySoil,
  handleFineDrySoil,
  kg,
}: StatusBoardSoilSectionProps) => {
  const { t, i18n } = useTranslation();
  const { isExpertMode } = useCroptimizer();

  const fineDrySoilTooltip = useMemo(
    () => (
      <div>
        <div>{t('fineDryTooltip1')}</div>
        <div>
          {t('example') +
            ' : 10 000 m2 x 0,2 m x 1,5 x 100% de terre fine (0% de cailloux) = 3 000 t/ha'}
        </div>
      </div>
    ),
    [t, i18n.language],
  );

  const fineDrySoilTitle = useMemo(
    () => t('fineDrySoil') + ' (cm ⓘ)',
    [t, i18n.language],
  );

  return (
    <div className={'border-b border-primitiveGrey pb-8'}>
      <div className="text-black font-bold text-xl md:text-lg md:mt-6 mb-8">
        {t('soil')}
      </div>
      <div className="flex mx-[-0.5rem] mt-4">
        <div className="w-1/3 md:w-full px-2">
          <SoilTypeField defaultSoilIndex={4} />
        </div>
        {!isExpertMode && (
          <div className="w-1/3 md:w-1/2 px-2">
            <MaskInput
              titleClassName={'top-[-9px] left-[8px] truncate max-w-[85%]'}
              title={fineDrySoilTitle}
              className="w-full"
              isBlueBg
              tooltipId="basic-dry"
              hasTooltip={fineDrySoilTooltip}
              value={fineDrySoil}
              onChange={(value) => setFineDrySoil(value)}
            />
            <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
              {handleFineDrySoil && <div> {handleFineDrySoil}</div>}
            </div>
          </div>
        )}
      </div>
      {isExpertMode &&
        <div className="flex mx-[-0.5rem] mt-4">
          <div className="w-1/3 md:w-1/2 px-2">
            <MaskInput
              titleClassName="top-[-9px] left-[8px]"
              title={`CEC (Cmol+/${kg})`}
              className="w-full"
              isBlueBg
              isGreyText={secSmol === initialSecSmol}
              value={secSmol}
              onChange={setSecSmol}
              redBorder={!!errorSecSmol}
            />
            <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
              {errorSecSmol && <div> {errorSecSmol}</div>}
            </div>
          </div>
          <div className="w-1/3 md:w-1/2 px-2">
            <MaskInput
              titleClassName={'top-[-9px] left-[8px] truncate max-w-[85%]'}
              title={fineDrySoilTitle}
              className="w-full"
              tooltipId="expert-dry"
              isBlueBg
              hasTooltip={fineDrySoilTooltip}
              value={fineDrySoil}
              onChange={(value) => setFineDrySoil(value)}
            />
            <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
              {handleFineDrySoil && <div> {handleFineDrySoil}</div>}
            </div>
          </div>
        </div>
      }
    </div>
  );
};
